/** Labels and option sets shared by the Filters panel and the active-filter chips. */
import type { AssetType, StatusFilter } from './search';
import type { AdNetworkAccount } from './api';
import { networkName, resolveNetwork } from '@/components/brand/networks';
import { STATUS_LABEL } from '@/components/ui/tag';

/** Glyph + word, so a status reads without colour. */
export const STATUS_FILTER_LABEL: Record<StatusFilter, string> = {
  ...STATUS_LABEL,
  inactive: 'Inactive',
  not_enough_data: 'Not enough data',
};

export const ASSET_TYPE_LABEL: Record<AssetType, string> = {
  image: 'Image',
  text: 'Text',
  video: 'Video',
  playable: 'Playable',
  site_link: 'Site link',
};

/** The panel offers these; `site_link` is accepted from a URL but not offered (as in apps/web). */
export const OFFERED_ASSET_TYPES: AssetType[] = ['image', 'text', 'video', 'playable'];

/** One network id per account (`facebook_ads` is Meta Ads). */
export const networkOf = (account: AdNetworkAccount): string => resolveNetwork(account.type) ?? account.type;

export const accountLabel = (account: AdNetworkAccount): string => account.name?.trim() || account.id;

export interface NetworkGroup {
  network: string;
  name: string;
  accountIds: string[];
}

/** Networks of the enabled accounts, sorted by name. */
export const networkGroups = (accounts: readonly AdNetworkAccount[]): NetworkGroup[] => {
  const groups = new Map<string, string[]>();
  for (const account of accounts) {
    const network = networkOf(account);
    groups.set(network, [...(groups.get(network) ?? []), account.id]);
  }
  return [...groups.entries()]
    .map(([network, accountIds]) => ({ network, name: networkName(network), accountIds }))
    .sort((a, b) => a.name.localeCompare(b.name));
};

/**
 * Toggling a network: turning it on selects all its accounts; turning it off
 * removes them. Other networks keep any account-level refinement (parity §0).
 */
export const toggleNetwork = (selected: readonly string[], group: NetworkGroup, on: boolean): string[] => {
  const own = new Set(group.accountIds);
  const others = selected.filter((id) => !own.has(id));
  return on ? [...others, ...group.accountIds] : others;
};

export const osOptionLabel = (os: string): string => {
  const lower = os.toLowerCase();
  return lower === 'ios' ? 'iOS' : lower === 'android' ? 'Android' : os;
};
